import {
    Component,
    DoCheck,
    Inject,
    IterableDiffers,
    OnDestroy,
    OnInit,
    ViewChild,
    AfterViewInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { TypingStatus } from '@sb-tools/typescript/enums/typing-status.enum';
import { TicketDetailsComponent } from '../../ticket-details.component';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { ComponentMessengerService } from 'src/app/core/services/component-messenger.service';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { ReplyStoreService } from 'src/app/core/services/ticket/reply-store.service';
import { IsInvalidMessagePipe } from '@sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';
import { UserDataService } from 'src/sb-tools/typescript/services/user-data.service';
import { QuillHelperService } from 'src/sb-tools/typescript/services/quill/quill-helper.service';
import { ReplyService } from 'src/app/core/services/ticket/reply.service';
import { NotificationsService } from 'angular2-notifications';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { NoteService } from 'src/app/core/services/note.service';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { SmilesService } from 'src/sb-tools/typescript/services/smiles.service';
import { ReplySortingService } from 'src/app/core/services/ticket/reply-sorting.service';
import { ReplyUpdateModel } from '@sb-tools/typescript/models/reply-update-model.model';
import { take } from 'rxjs/operators';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { ThreadFilterService } from '../../../core/services/thread-filter.service';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { ReplyTemplateService } from '../../../../sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { SatisfactionDTO } from '../../../core/models/dto/satisfaction.dto';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { TypingStatusUpdatedEventModel } from '../../../core/models/dto/signalR/typing-status-updated-event-model.model';
import { TicketAuthorOnlineStatusUpdatedEventModel } from '../../../core/models/dto/signalR/ticket-author-online-status-updated-event-model.model';
import { ReplyPropertiesUpdatedEventModel } from '../../../core/models/dto/signalR/reply-properties-updated-event-model.model';
import { SatisfactionEventModel } from '../../../core/models/dto/signalR/satisfaction-event-model.model';
import { TicketSurveyAddedEventModel } from '../../../core/models/dto/signalR/ticket-survey-added-event-model.model.ts';
import { DatePipe, NgClass } from '@angular/common';
import { HighlightOnChangeDirective } from '../../../shared/directives/highlight-on-change.directive';
import { FieldsComponent } from '../../fields/fields.component';
import { IntegrationBlockComponent } from '../../integration-block/integration-block.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ReplyChatComponent } from './reply-chat/reply-chat.component';
import { SuggestionsComponent } from '../suggestions/suggestions.component';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { QuillReplyTemplatesComponent } from '../../../../sb-modules/Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { QuillEditorComponent } from 'ngx-quill';
import { FormsModule } from '@angular/forms';
import { QuillEmojiPickerComponent } from '@sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { FileViewComponent } from '@sb-document-viewer/components/file-view/file-view.component';
import { FilePreviewComponent } from '@sb-document-viewer/components/file-preview/file-preview.component';
import { GiphyPickerComponent } from '../../../../sb-modules/Frontend-GIF-Module/giphy-picker/giphy-picker.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { IsInvalidMessagePipe as IsInvalidMessagePipe_1 } from '../../../../sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';
import { FilterContentAttachmentsPipe } from '../../../shared/pipes/filter-content-attachments.pipe';
import { SurveyDTO } from '../../../core/models/dto/survey.dto';

@Component({
    selector: 'app-ticket-thread-chat',
    templateUrl: './ticket-thread-chat.component.html',
    styleUrls: ['./ticket-thread-chat.component.scss'],
    imports: [
        NgClass,
        DatePipe,
        HighlightOnChangeDirective,
        FieldsComponent,
        IntegrationBlockComponent,
        InfiniteScrollModule,
        ReplyChatComponent,
        TranslateModule,
        SuggestionsComponent,
        UserAvatarComponent,
        QuillReplyTemplatesComponent,
        QuillEditorComponent,
        FormsModule,
        QuillEmojiPickerComponent,
        InlineSVGComponent,
        FileViewComponent,
        FilePreviewComponent,
        GiphyPickerComponent,
        TooltipModule,
        PopoverModule,
        IsInvalidMessagePipe_1,
        FilterContentAttachmentsPipe,
    ],
})
export class TicketThreadChatComponent implements OnInit, DoCheck, OnDestroy, AfterViewInit {
    @ViewChild('sendButtonConfigPopover') sendButtonConfigPopover;

    attachmentTypeEnum = AttachmentType;
    autoSignatureHTMLContent: string;
    chatReplies: (ThreadElementDTO | SatisfactionDTO | SurveyDTO)[] = [];
    chatRepliesLoaded = [];
    isOnlyAutoSignatureSet = false;
    isSendingByEnterCurrentValue = false;
    isSendingByEnterNewValue = false;
    newReply: ReplyUpdateModel;
    quillConfig: unknown;
    threadFilterService = ThreadFilterService;
    ticket: DetailedTicketDTO;
    ticketSourceEnum = TicketSource;
    typingInfos: TypingStatusUpdatedEventModel[] = [];

    private count = 20;
    private iterableDiffer;
    private loadedCount = this.count;
    private sendButtonConfigPopoverTimerToHide;
    private sendButtonConfigPopoverTimerToShow;
    private typing = {
        timerId: undefined,
        status: TypingStatus.none,
        isStartTyping: false,
    };
    private unsubscriber: Subject<void> = new Subject<void>();

    constructor(
        @Inject(TicketDetailsComponent) public parent: TicketDetailsComponent,
        public commonDataService: CommonDataService,
        public replyTemplateService: ReplyTemplateService,
        private signalRService: SignalRService,
        private userDataService: UserDataService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private componentMessengerService: ComponentMessengerService,
        private smilesService: SmilesService,
        private replyStoreService: ReplyStoreService,
        private quillHelperService: QuillHelperService,
        private replyService: ReplyService,
        private noteService: NoteService,
        private replySortingService: ReplySortingService,
        private iterableDiffers: IterableDiffers,
        private ticketService: TicketService,
    ) {
        this.ticket = parent.ticket;
        this.iterableDiffer = this.iterableDiffers.find([]).create(null);
    }

    ngOnInit(): void {
        this.newReply = this.parent.newReply;

        this.processChatReplies();

        this.parent.isCustomerOnline = true;

        this.setupSignalRListeners();

        this.componentMessengerService.newReplyMessage.pipe(takeUntil(this.unsubscriber)).subscribe((chatMessage) => {
            if (
                !this.ticket.messageThread.find((chatReply) => {
                    return chatReply.id === chatMessage.id;
                })
            ) {
                this.ticket.messageThread.push(chatMessage);
                this.processChatReplies(true);
            }

            return;
        });

        this.componentMessengerService.showAllChatRepliesMessage.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.loadedCount = this.chatReplies.length;
            this.processChatReplies();
        });

        this.isSendingByEnterCurrentValue = this.commonDataService.user.isChatSendingByEnter;
        this.isSendingByEnterNewValue = this.isSendingByEnterCurrentValue;

        this.updateContentHeight();
        this.initQuillConfig(this.handleQuillEnterClick.bind(this));
    }

    ngDoCheck(): void {
        const changes = this.iterableDiffer.diff(this.newReply.attachments);
        if (changes) {
            setTimeout(() => {
                this.updateContentHeight();
            }, 0);
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            const scrollContainer = document.getElementById('scroll-container') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }, 200);
    }

    addAutoSignature(): void {
        this.newReply.message.text = HelperService.strip(
            this.smilesService.replaceImagesToCodes(this.autoSignatureHTMLContent),
            false,
        );
    }

    addGifAttachment(gif: AttachmentBase): void {
        this.newReply.attachments.push(gif);
        this.updateContentHeight();

        setTimeout(() => {
            const element = document.getElementById('scroll-container');

            element?.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth',
            });
        }, 300);
    }

    deleteGifAttachment(attachment: AttachmentBase): void {
        const index = this.newReply.attachments.indexOf(attachment);
        this.newReply.attachments.splice(index, 1);
    }

    hideSendButtonConfigPopover(): void {
        clearTimeout(this.sendButtonConfigPopoverTimerToShow);
        this.sendButtonConfigPopoverTimerToHide = setTimeout(() => {
            this.sendButtonConfigPopover.hide();
        }, 500);
    }

    loadMore(): void {
        if (this.threadFilterService.isPrinting) {
            return;
        }

        if (this.loadedCount >= this.chatReplies.length) {
            return;
        }

        this.loadedCount += this.count;

        this.processChatReplies();
    }

    onReplyChanged(data, reply: ReplyUpdateModel, isNewReply?) {
        reply.message.text = HelperService.strip(this.smilesService.replaceImagesToCodes(reply.message.html), false);

        if (isNewReply) {
            this.isOnlyAutoSignatureSet = data.html && !this.newReply.message.html;

            if (this.isOnlyAutoSignatureSet) {
                this.autoSignatureHTMLContent = data.html;
            }

            this.replyStoreService.saveTempReply(this.newReply.message);
            this.replyService.controlVisibilityOfNewReplyField(this.replySortingService.replySortBy, true);
        }
        this.updateUserTypingStatus();
        this.updateContentHeight();
    }

    showSendButtonConfigPopover(): void {
        clearTimeout(this.sendButtonConfigPopoverTimerToHide);
        this.sendButtonConfigPopoverTimerToShow = setTimeout(() => {
            this.sendButtonConfigPopover.show();
        }, 1500);
    }

    triggerUserIdentification(): void {
        this.ticketService.createRequestIdentification(this.ticket.id).pipe(takeUntil(this.unsubscriber)).subscribe();
    }

    updateChatSendingByEnter(): void {
        if (this.isSendingByEnterNewValue !== this.isSendingByEnterCurrentValue) {
            this.userDataService
                .updateChatSendingByEnter(this.commonDataService.user.id, this.isSendingByEnterNewValue)
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe(() => {
                    this.isSendingByEnterCurrentValue = this.isSendingByEnterNewValue;
                    this.commonDataService.user.isChatSendingByEnter = this.isSendingByEnterNewValue;
                });
        }
    }

    updateContentHeight(): void {
        const chatContainer = document.getElementById('ticket-thread-chat-new-reply');
        const scrollContainer = document.getElementById('scroll-container');

        if (chatContainer && scrollContainer) {
            scrollContainer.style.height = 'calc(100% - ' + chatContainer?.offsetHeight + 'px)';
        }
    }

    private checkIsTicketCanBeReplied(): void {
        this.ticket.isTicketCanBeReplied = this.replyService.getIsTicketCanBeRepliedStatus(
            this.ticket,
            this.commonDataService.user.id,
            this.parent.isCustomerOnline,
        );

        if (this.ticket.isTicketCanBeReplied) {
            this.updateContentHeight();
        }
    }

    private handleQuillEnterClick(isOnlyEnterKey: boolean): boolean {
        const isSentByEnter = isOnlyEnterKey && this.isSendingByEnterCurrentValue;
        const isSentByCtrlEnter = !isOnlyEnterKey && !this.isSendingByEnterCurrentValue;

        if (isSentByEnter || isSentByCtrlEnter) {
            this.sendReplyByShortcutCall();
            return false;
        } else {
            return true;
        }
    }

    private initQuillConfig(enterKeyHandler: (submit: boolean) => boolean): void {
        this.quillConfig = {
            toolbar: false,
            imageDrop: {
                saveAsAttachment: (file) => {
                    this.quillHelperService.saveAsAttachment(this.newReply.attachments, file);
                },
                isAddingAsAttachment: true,
            },
            keyboard: {
                bindings: {
                    enter: {
                        key: 'Enter',
                        handler: () => {
                            return enterKeyHandler(true);
                        },
                    },

                    linebreak: {
                        key: 'Enter',
                        shortKey: true, // cmd or ctrl key
                        handler: () => {
                            return enterKeyHandler(false);
                        },
                    },
                },
            },
        };
    }

    private processChatReplies(isUpdate?: boolean): void {
        if (isUpdate) {
            this.chatReplies.push(this.ticket.messageThread[this.ticket.messageThread.length - 1]);
            const scrollContainer = document.getElementById('scroll-container') as HTMLElement;
            const isNeedToScroll =
                scrollContainer &&
                scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight) < 200;
            if (isNeedToScroll) {
                setTimeout(() => {
                    scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
                }, 100);
            } else if (
                this.ticket.messageThread[this.ticket.messageThread.length - 1]['author']?.id !==
                this.commonDataService.user.id
            ) {
                this.notificationsService.info(
                    this.translateService.instant('Socialboards Chat'),
                    this.translateService.instant('You have received a new reply below'),
                );
            }
        } else {
            const firstChatMessage: ThreadElementDTO = {
                message: this.ticket.message,
                type: MessageThreadElementType.reply,
                author: this.ticket.firstMessageCreator,
                creationTime: this.ticket.firstMessageCreationTime,
                attachments: this.ticket.attachments,
                isDeleted: this.ticket.isFirstMessageDeleted,
            } as any;
            const hideInitiationMessage =
                this.ticket.detailedSource === DetailedTicketSource.chatPublicAPI &&
                this.ticket.isTechnicalChatInitiationMessage === true &&
                !this.ticket.message?.text?.trim() &&
                !this.ticket.message?.html?.trim() &&
                !this.ticket.attachments?.length &&
                !this.ticket.isFirstMessageDeleted;
            this.chatReplies = hideInitiationMessage
                ? [...this.ticket.messageThread]
                : [firstChatMessage, ...this.ticket.messageThread];
        }

        this.chatReplies = this.chatReplies.filter(
            (entry) => entry.type !== MessageThreadElementType.sessionAuthentication,
        );

        if (this.loadedCount < this.count && this.chatReplies.length <= this.count) {
            this.loadedCount = this.count;
        }

        let sliceIndex = this.chatReplies.length - this.loadedCount;

        if (sliceIndex < 0) {
            sliceIndex = 0;
        }

        this.chatRepliesLoaded = this.chatReplies.slice(sliceIndex);
    }

    private processTypingStatusChanges(typingInfo: TypingStatusUpdatedEventModel): void {
        if (typingInfo.ticketId !== this.ticket.id) {
            return;
        }

        const info = this.typingInfos.find((existedTypingInfo: TypingStatusUpdatedEventModel) => {
            return existedTypingInfo.user.id === typingInfo.user.id;
        });

        switch (typingInfo.status) {
            case TypingStatus.none:
                this.typingInfos.splice(this.typingInfos.indexOf(info), 1);
                break;
            case TypingStatus.typing:
            case TypingStatus.deleting:
                if (info) {
                    info.status = typingInfo.status;
                } else {
                    this.typingInfos.push(typingInfo);
                }
                break;
        }
    }

    private sendReplyByShortcutCall(): void {
        if (
            !(
                new IsInvalidMessagePipe().transform(
                    this.newReply.message,
                    this.ticket.constraints,
                    !!this.newReply.attachments.length,
                    false,
                ) || this.parent.isSendingReply
            )
        ) {
            if (this.ticket.constraints.isTypingStatusAllowed) {
                this.typing.status = TypingStatus.none;

                const typingStatus: TypingStatusUpdatedEventModel = {
                    ticketId: this.ticket.id,
                    source: this.ticket.detailedSource,
                    status: TypingStatus.none,
                    user: this.commonDataService.user,
                };
                this.signalRService.updateReplyTypingStatus(typingStatus).pipe(take(1)).subscribe();
            }

            this.newReply.isNeedToBeSentByEmail = false;
            this.parent.addReply(this.newReply);
        }
    }

    private setupSignalRListeners(): void {
        this.signalRService
            .onConnected()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                if (this.ticket.detailedSource !== DetailedTicketSource.chat) {
                    return;
                }
                this.signalRService
                    .isTicketAuthorOnline(this.ticket.id)
                    .pipe(take(1))
                    .subscribe((isOnline: boolean) => {
                        this.parent.isCustomerOnline = isOnline;
                        this.checkIsTicketCanBeReplied();
                    });
            });

        this.signalRService
            .onReplyTypingStatusUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TypingStatusUpdatedEventModel) => {
                return this.processTypingStatusChanges(data);
            });

        this.signalRService
            .onTicketAuthorOnlineStatusUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketAuthorOnlineStatusUpdatedEventModel) => {
                if (data.ticketId === this.ticket.id) {
                    this.parent.isCustomerOnline = data.isOnlineStatus;
                    this.checkIsTicketCanBeReplied();
                    if (data.isOnlineStatus === false) {
                        this.typing.status = TypingStatus.none;
                        this.typingInfos = [];
                    }
                }
            });

        this.signalRService
            .isTicketAuthorOnline(this.ticket.id)
            .pipe(take(1))
            .subscribe((isOnline: boolean) => {
                this.parent.isCustomerOnline = isOnline;
                this.checkIsTicketCanBeReplied();
            });

        this.signalRService
            .onReplyPropertiesUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: ReplyPropertiesUpdatedEventModel) => {
                if (this.ticket?.id === data.ticketId) {
                    const reply = this.chatReplies.find(
                        (currentReply) => currentReply.id === data.replyId,
                    ) as ThreadElementDTO;
                    if (reply) {
                        Object.assign(reply, data.replyProperties);
                        this.threadFilterService.refreshUpdateStamp();
                    }
                }
            });

        this.signalRService
            .onSatisfactionAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: SatisfactionEventModel) => {
                if (this.ticket && this.ticket.id === data.ticketId) {
                    this.ticket.messageThread.push(data.satisfaction);
                    this.processChatReplies(true);
                }
            });

        this.signalRService
            .onSatisfactionUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: SatisfactionEventModel) => {
                if (this.ticket && this.ticket.id === data.ticketId) {
                    const index = this.chatReplies.findIndex((reply) => {
                        return reply.type === MessageThreadElementType.satisfaction;
                    });
                    (this.chatReplies[index] as SatisfactionDTO).text = data.satisfaction.text;
                }
            });

        this.signalRService
            .onSurveyAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: TicketSurveyAddedEventModel) => {
                if (this.ticket && this.ticket.id === data.ticketId) {
                    this.ticket.messageThread.push(data.survey);
                    this.processChatReplies(true);
                }
            });
    }

    private updateUserTypingStatus(): void {
        if (!this.ticket.constraints.isTypingStatusAllowed) {
            return;
        }

        clearTimeout(this.typing.timerId);

        if (this.typing.status !== TypingStatus.typing) {
            this.typing.status = TypingStatus.typing;
            const typingStatus: TypingStatusUpdatedEventModel = {
                ticketId: this.ticket.id,
                source: this.ticket.detailedSource,
                status: TypingStatus.typing,
                user: this.commonDataService.user,
            };
            this.signalRService.updateReplyTypingStatus(typingStatus).pipe(take(1)).subscribe();
        }

        this.typing.timerId = setTimeout(() => {
            this.typing.status = TypingStatus.none;
            const typingStatus: TypingStatusUpdatedEventModel = {
                ticketId: this.ticket.id,
                source: this.ticket.detailedSource,
                status: TypingStatus.none,
                user: this.commonDataService.user,
            };
            this.signalRService.updateReplyTypingStatus(typingStatus).pipe(take(1)).subscribe();
        }, 1000);
    }

    ngOnDestroy(): void {
        this.noteService.isReplyAlreadySelected = false;

        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
